import type { ErrorCode, FieldProblem, Message, ProblemDetails } from '../types/index';

// Compile-time exhaustive: a code added to the contract's `ErrorCode` breaks the build here until it is listed.
const KNOWN_CODES: Record<ErrorCode, true> = {
  validation_failed: true,
  not_found: true,
  publish_disabled: true,
  not_demo: true,
  change_blocked: true,
  conflict_stale_change: true,
  undo_expired: true,
  refresh_too_soon: true,
  rate_limited: true,
  connection_failed: true,
  not_ready: true,
  plan_limit_reached: true,
  internal_error: true,
  network_error: true,
  timeout: true,
  malformed_answer: true, // client-side (T-364, D-892): a period-keyed answer with no `meta.period`
};

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(KNOWN_CODES, value);
}

export interface ApiErrorInit {
  /** HTTP status; absent for the errors the client synthesises (`network_error`, `timeout`). */
  status?: number;
  /** The server's problem document (API §1.8), when there was one. */
  problem?: ProblemDetails;
  retryAfterSeconds?: number | null;
  cause?: unknown;
}

/**
 * The one error type both `ApiClient` implementations throw (ARCH §8.2, API §1.8). UIs switch on `code`
 * and render `userMessage`; `message` (from `Error`) is developer text and is never shown to a user.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number | null;
  readonly problem: ProblemDetails | null;
  readonly retryAfterSeconds: number | null;

  constructor(code: ErrorCode, init: ApiErrorInit = {}) {
    const detail = init.problem?.detail ?? init.problem?.title;
    super(
      detail ? `${code}: ${detail}` : code,
      init.cause === undefined ? undefined : { cause: init.cause },
    );
    this.name = 'ApiError';
    this.code = code;
    this.status = init.status ?? init.problem?.status ?? null;
    this.problem = init.problem ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? init.problem?.retryAfterSeconds ?? null;
  }

  /** What a UI renders through `@sl/core` messages: the server's message, else the catalogue's `error.<code>`. */
  get userMessage(): Message {
    return this.problem?.message ?? { key: `error.${this.code}`, params: {} };
  }

  /** Field-level problems of a `validation_failed` / `change_blocked` answer; empty otherwise. */
  get fieldErrors(): FieldProblem[] {
    return this.problem?.errors ?? [];
  }
}

/** Survives duplicated module instances (two bundles, Jest module registries), where `instanceof` does not. */
export function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError ||
    (error instanceof Error && error.name === 'ApiError' && isErrorCode((error as ApiError).code))
  );
}
